import React from 'react';
import PageHero from './PageHero';
import {
  GraduationCap,
  Route,
  Landmark,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  BookOpen,
  Award
} from 'lucide-react';
import heroImg from '../assets/Banner-GMIS Students.png';
import ibLogo from '../assets/IB CONTINUUM.png';
import cambridgeLogo from '../assets/CAMBRIDGE.png';
import tutWuriLogo from '../assets/TUT WURI HANDAYANI.png';
import bseisiLogo from '../assets/BSEISI.png';

export default function CurriculumApproachPage({ setPage, t }) {
  const content = t?.curriculumApproach || {};
  const dist = content?.distinguishes || {};
  const distCards = dist?.cards || [];
  const phil = content?.philosophy || {};
  const pillars = phil?.pillars || [];
  const curricula = content?.curricula || {};
  const curriculaItems = curricula?.items || [];
  const cta = content?.cta || {};

  const handleNavigate = (pageKey) => {
    if (setPage) setPage(pageKey);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const curriculaLogos = {
    ib: ibLogo,
    cambridge: cambridgeLogo,
    national: tutWuriLogo,
    bseisi: bseisiLogo
  };

  const stageLinks = [
    {
      stage: 'Preschool',
      age: 'Ages 2–6',
      desc: 'Early Years inquiry, play-based sensory exploration and foundational literacy.',
      route: 'preschool'
    },
    {
      stage: 'Primary School',
      age: 'Grades 1–5 (IB PYP)',
      desc: 'Structured inquiry, conceptual mastery, and holistic interdisciplinary learning.',
      route: 'primary-school'
    },
    {
      stage: 'Middle School',
      age: 'Grades 6–8 (IB MYP & Cambridge)',
      desc: 'Dual academic pathway fostering critical analysis and subject excellence.',
      route: 'middle-school'
    },
    {
      stage: 'Senior School',
      age: 'Grades 9–12 (IB DP, CP & IGCSE)',
      desc: 'Pre-university rigor with global university entrance and scholarship success.',
      route: 'senior-school'
    }
  ];

  const navCategory = t?.nav?.[2]?.label || 'Academics';
  const homeLabel = t?.nav?.[0]?.label || 'Home';

  return (
    <div className="w-full bg-[#f8f9fa] min-h-screen text-ink pb-0">

      {/* ── Page Hero ──────────────────────────────────────────────────────── */}
      <PageHero
        eyebrow={content.eyebrow || navCategory}
        title={content.title || 'Curriculum and Approach'}
        subtitle={
          content.subtitle ||
          'A diverse, globally recognised education fostering student-centred inquiry, conceptual mastery, and lifelong global perspective.'
        }
        bgImage={heroImg}
        breadcrumbs={content.breadcrumbs || [homeLabel, navCategory, 'Curriculum and Approach']}
      />

      <div className="max-w-[1240px] mx-auto px-6 lg:px-[60px] pt-16 lg:pt-20">

        {/* ── Section 1: What Distinguishes GMIS Curriculum ────────────────── */}
        <div className="bg-[#f5f8fc] border border-[#e2e8f0]/80 rounded-[28px] p-8 md:p-14 mb-16 shadow-[0_2px_16px_rgba(15,23,42,0.03)]">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="m-0 font-heading font-medium text-[32px] md:text-[38px] text-[#1e3a8a] tracking-tight leading-tight mb-3 uppercase">
              {dist.title || 'What Distinguishes GMIS Curriculum'}
            </h2>
            <p className="m-0 text-[14.5px] md:text-[15.5px] text-gray-600 leading-relaxed max-w-2xl mx-auto">
              {dist.subtitle ||
                'Our commitment to educational excellence extends beyond accreditation to create a truly distinctive learning environment.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7">
            {distCards.map((card, idx) => {
              const icons = [
                <GraduationCap key="grad" className="w-6 h-6 text-white" />,
                <Route key="route" className="w-6 h-6 text-white" />,
                <Landmark key="landmark" className="w-6 h-6 text-white" />
              ];

              return (
                <div
                  key={idx}
                  className="bg-white rounded-2xl p-8 border border-slate-100 shadow-[0_8px_25px_rgba(0,0,0,0.04)] hover:shadow-lg transition-all duration-300 flex flex-col items-center text-center group"
                >
                  <div className="w-14 h-14 rounded-full bg-[#1b3b6f] text-white flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300 shadow-sm">
                    {icons[idx] || <Award className="w-6 h-6 text-white" />}
                  </div>

                  <h3 className="m-0 font-heading font-bold text-[19px] text-[#1b3b6f] mb-3">
                    {card.title}
                  </h3>

                  <p className="m-0 text-[13.5px] md:text-[14px] text-gray-500 leading-[1.65]">
                    {card.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Section 2: Educational Philosophy & Student-Centred Learning ──── */}
        <div className="mb-20 max-w-5xl mx-auto px-2">
          <div className="space-y-6 text-[18px] md:text-[21px] text-slate-800 leading-[1.7] font-normal tracking-[-0.01em]">
            <p className="m-0">
              {phil.p1 ||
                'The curriculum is designed around student-centred and inquiry-based learning, encouraging students to explore ideas, ask meaningful questions, and develop a strong conceptual understanding. Learning extends beyond the classroom, with a focus on critical thinking, creativity, practical skills, and meaningful real-world application.'}
            </p>

            <p className="m-0 text-[16.5px] md:text-[18.5px] text-slate-800 leading-[1.75]">
              {phil.p2 ||
                'Students have access to a diverse range of recognised curricula, including the Indonesian National Curriculum, International Baccalaureate (IB), Cambridge IGCSE (UK) and the Board of Secondary Education Indian Schools Indonesia (BSEISI), which is equivalent to the Central Board of Secondary Education (CBSE), India.'}
            </p>

            <p className="m-0 text-[16.5px] md:text-[18.5px] text-slate-800 leading-[1.75]">
              {phil.p3 ||
                'Together, these curriculum pathways provide a strong academic foundation while developing students’ knowledge, skills, adaptability, and global perspective, preparing them for further education and an increasingly interconnected world.'}
            </p>
          </div>

          {/* Core Inquiry Pillars */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-10 mt-10 border-t border-slate-200">
            {pillars.map((pillar, idx) => (
              <div key={idx} className="p-5 rounded-xl bg-white border border-slate-200/80 shadow-sm hover:border-gold transition-colors">
                <div className="flex items-center gap-2.5 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-gold shrink-0" />
                  <h4 className="m-0 font-heading font-bold text-navy text-[15.5px]">
                    {pillar.title}
                  </h4>
                </div>
                <p className="m-0 text-[13px] text-ink/75 leading-relaxed pl-7">
                  {pillar.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Section 3: Recognised Curriculum Frameworks ─────────────────── */}
        <div className="mb-20">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 mb-2 px-3 py-1 rounded-full border border-gold/30 bg-gold/10">
              <Sparkles className="w-3.5 h-3.5 text-gold" />
              <span className="text-gold-ink text-[11px] font-bold tracking-[0.2em] uppercase">
                {curricula.eyebrow || 'RECOGNISED PATHWAYS'}
              </span>
            </div>
            <h2 className="m-0 font-heading font-semibold text-[32px] md:text-[38px] text-navy mb-3 uppercase">
              {curricula.title || 'Accredited Global Curriculum Frameworks'}
            </h2>
            <p className="m-0 text-[16px] text-ink/75">
              {curricula.subtitle ||
                'Offering international rigour alongside national excellence for diverse student aspirations.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {curriculaItems.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-2xl p-8 border border-line shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between gap-4 mb-6 pb-4 border-b border-line/60">
                    <div className="h-14 flex items-center">
                      <img
                        src={curriculaLogos[item.id]}
                        alt={item.name}
                        className="max-h-12 w-auto object-contain"
                      />
                    </div>
                    <span className="px-3 py-1 rounded-full text-[11px] font-heading font-semibold uppercase tracking-wider bg-gold/15 text-gold-ink border border-gold/30">
                      {item.badge}
                    </span>
                  </div>

                  <h3 className="m-0 font-heading font-bold text-[20px] text-navy mb-1">
                    {item.name}
                  </h3>
                  <div className="text-[13px] font-medium text-gold-ink mb-3">
                    {item.subtitle}
                  </div>

                  <p className="m-0 text-[14.5px] text-ink/75 leading-relaxed">
                    {item.desc}
                  </p>
                </div>

                <div className="pt-6 mt-6 border-t border-line/60 flex items-center justify-between text-xs font-semibold text-navy">
                  <span className="text-ink/60">Recognised Worldwide</span>
                  <div className="flex items-center gap-1.5 text-gold-ink">
                    <CheckCircle2 className="w-4 h-4 text-gold" />
                    <span>Accredited Programme</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Section 4: Academic Stages Exploration ──────────────────────── */}
        <div className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h3 className="m-0 font-heading font-semibold text-[26px] md:text-[32px] text-navy mb-2">
              {t?.utilAdm === 'Penerimaan' ? 'Jelajahi Jenjang Pendidikan' : 'Explore Educational Stages'}
            </h3>
            <p className="m-0 text-[15px] text-ink/70">
              {t?.utilAdm === 'Penerimaan'
                ? 'Dari prasekolah hingga sekolah menengah atas, kami menyediakan lingkungan belajar yang berkelanjutan.'
                : 'From early childhood through senior school graduation, each stage prepares learners for the next milestone.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {stageLinks.map((stage) => (
              <div
                key={stage.route}
                onClick={() => handleNavigate(stage.route)}
                className="bg-white rounded-xl p-6 border border-line shadow-sm hover:shadow-lg hover:border-gold transition-all duration-300 cursor-pointer group flex flex-col justify-between"
              >
                <div>
                  <div className="w-10 h-10 rounded-lg bg-navy/5 text-navy flex items-center justify-center mb-4 group-hover:bg-gold group-hover:text-navy-deep transition-colors">
                    <BookOpen className="w-5 h-5" />
                  </div>
                  <h4 className="m-0 font-heading font-bold text-[18px] text-navy mb-1 group-hover:text-gold-ink transition-colors">
                    {stage.stage}
                  </h4>
                  <div className="text-xs font-bold text-gold-ink uppercase tracking-wider mb-3">
                    {stage.age}
                  </div>
                  <p className="m-0 text-[13px] text-ink/70 leading-relaxed">
                    {stage.desc}
                  </p>
                </div>

                <div className="pt-4 mt-4 border-t border-line/60 flex items-center justify-between text-xs font-bold text-navy group-hover:text-gold-ink">
                  <span>Explore Programme</span>
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* ── Section 5: CTA (PreschoolPage style, solid bg-navy with NO gradient) ── */}
      <div className="w-full bg-white mt-12">
        <div className="max-w-[1200px] mx-auto px-8 py-12">
          <div className="relative overflow-hidden bg-navy rounded-3xl p-12 md:p-20 text-center shadow-2xl">
            <div className="relative z-10">
              <h3 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6 leading-tight uppercase">
                {cta.title || "Ready to Begin Your Child's Journey?"}
              </h3>
              <p className="text-white/70 text-xl max-w-2xl mx-auto mb-10">
                {cta.subtitle ||
                  'Schedule a campus visit today and see our world-class educational programmes and facilities for yourself.'}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={() => handleNavigate('schedule-visit')}
                  className="bg-gold text-navy px-10 py-4 rounded-full font-bold text-lg tracking-wide hover:bg-white hover:text-navy transition-colors duration-300 cursor-pointer"
                >
                  {cta.button || 'Schedule a Visit'}
                </button>
                <button
                  onClick={() => handleNavigate('admissions-process')}
                  className="bg-white/10 text-white px-10 py-4 rounded-full font-bold text-lg tracking-wide hover:bg-white/20 transition-colors duration-300 border border-white/20 cursor-pointer"
                >
                  {cta.secondaryButton || 'Explore Admissions'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
